import api from './api';

/**
 * VisitService
 * Handles visit timing and workflow operations
 */
class VisitService {
  /**
   * Send patient to bus (Intake specialist action)
   * Records intakeSentTime and calculates Timer 1
   * @param {string} visitId - Visit MongoDB ID
   * @returns {Promise} - API response
   */
  async sendToBus (visitId) {
    try {
      const response = await api.post(`/visits/${visitId}/send-to-bus`);
      return response; // Return full response: { success, message, data }
    } catch (error) {
      console.error('Error sending patient to bus:', error);
      throw error;
    }
  }

  /**
   * Start screening (Bus screener action)
   * Records screeningStartTime and calculates Timer 2
   * @param {string} visitId - Visit MongoDB ID
   * @returns {Promise} - API response
   */
  async startScreening (visitId) {
    try {
      const response = await api.post(`/visits/${visitId}/start-screening`);
      return response; // Return full response: { success, message, data }
    } catch (error) {
      console.error('Error starting screening:', error);
      throw error;
    }
  }

  /**
   * Complete screening (Bus screener action)
   * Records screeningCompleteTime, calculates Timer 3, and sends notification
   * @param {string} visitId - Visit MongoDB ID
   * @returns {Promise} - API response
   */
  async completeScreening (visitId) {
    try {
      const response = await api.post(`/visits/${visitId}/complete-screening`);
      return response; // Return full response: { success, message, data }
    } catch (error) {
      console.error('Error completing screening:', error);
      throw error;
    }
  }

  /**
   * Save progress without completing the visit
   * Allows partial saves for multi-user intake workflow
   * The visit stays in the active queue and timer continues running
   * @param {string} visitId - Visit MongoDB ID
   * @param {string[]} sectionsUpdated - Optional array of section names that were updated
   * @returns {Promise} - API response
   */
  async saveProgress (visitId, sectionsUpdated = []) {
    try {
      const response = await api.post(`/visits/${visitId}/save-progress`, { sectionsUpdated });
      return response;
    } catch (error) {
      console.error('Error saving progress:', error);
      throw error;
    }
  }

  /**
   * Get timing data for a visit
   * @param {string} visitId - Visit MongoDB ID
   * @returns {Promise} - API response with timing data
   */
  async getVisitTiming (visitId) {
    try {
      const response = await api.get(`/visits/${visitId}/timing`);
      return response.data;
    } catch (error) {
      console.error('Error fetching visit timing:', error);
      throw error;
    }
  }

  /**
   * Activate a pre-registered visit (moves to active queue, starts timer)
   * @param {string} visitId - Visit MongoDB ID
   * @returns {Promise} - API response
   */
  async activatePreRegistration (visitId) {
    try {
      const response = await api.post(`/visits/${visitId}/activate`);
      return response;
    } catch (error) {
      console.error('Error activating pre-registration:', error);
      throw error;
    }
  }

  /**
   * Activate all pre-registered visits for an event
   * @param {string} bookingId - Booking/Event ID
   * @returns {Promise} - API response
   */
  async activateEventPreRegistrations (bookingId) {
    try {
      const response = await api.post(`/visits/activate-event/${bookingId}`);
      return response;
    } catch (error) {
      console.error('Error activating event pre-registrations:', error);
      throw error;
    }
  }

  /**
   * Format duration in seconds to human-readable format
   * @param {number} seconds - Duration in seconds
   * @returns {string} - Formatted duration (e.g., "2m 30s")
   */
  formatDuration (seconds) {
    if (!seconds || seconds < 0) return '-';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  }

  /**
   * Format duration in seconds with units (e.g., "2d 5h" or "1h 23m" or "5m 30s")
   * Always shows 2 parts maximum for readability
   * @param {number} seconds - Duration in seconds
   * @returns {string} - Formatted duration
   */
  formatDurationClock (seconds) {
    if (!seconds || seconds < 0) return '0s';

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) {
      // Show days and hours only (e.g., "2d 5h" or just "2d")
      if (hours > 0) {
        return `${days}d ${hours}h`;
      }
      return `${days}d`;
    } else if (hours > 0) {
      // Show hours and minutes (e.g., "1h 23m")
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      // Show minutes and seconds (e.g., "5m 30s")
      return `${minutes}m ${secs}s`;
    } else {
      // Just seconds (e.g., "45s")
      return `${secs}s`;
    }
  }

  /**
   * Calculate elapsed time since a timestamp
   * @param {Date|string} timestamp - Start timestamp
   * @returns {number} - Elapsed seconds
   */
  calculateElapsedTime (timestamp) {
    if (!timestamp) return 0;
    const start = new Date(timestamp);
    const now = new Date();
    return Math.floor((now - start) / 1000);
  }

  /**
   * Get the effective start time for timer calculations
   * Uses timerStartTime if available (for pre-registrations), otherwise falls back to checkInTime
   * @param {Object} record - Visit record with timing fields
   * @returns {Date|null} - The effective start time to use for timer calculations
   */
  getEffectiveTimerStart (record) {
    if (!record) return null;
    // Use timerStartTime if available (set when pre-registration is activated)
    // Otherwise fall back to checkInTime for day-of registrations
    return record.timerStartTime || record.checkInTime;
  }

  /**
   * Check if a visit is a pre-registration that hasn't been activated yet
   * @param {Object} record - Visit record
   * @returns {boolean} - True if visit is pending activation
   */
  isPendingActivation (record) {
    return record?.status === 'pre_registered' || 
           (record?.isPreRegistration && !record?.timerStartTime);
  }

  /**
   * Change visit event (reassign visit to a different booking/event)
   * @param {string} visitId - Visit MongoDB ID or visitId string
   * @param {string} bookingId - New booking/event ID
   * @param {string} reason - Optional reason for the change
   * @returns {Promise} - API response
   */
  async changeVisitEvent(visitId, bookingId, reason = '') {
    try {
      const response = await api.patch(`/visits/${visitId}/event`, { bookingId, reason });
      return response;
    } catch (error) {
      console.error('Error changing visit event:', error);
      throw error;
    }
  }

  /**
   * Get events available for reassignment (all past events + up to 10 future events)
   * @returns {Promise} - API response with events list
   */
  async getEventsForReassignment() {
    try {
      const response = await api.get('/visits/events-for-reassignment');
      return response;
    } catch (error) {
      console.error('Error fetching events for reassignment:', error);
      throw error;
    }
  }

  /**
   * Get all visits for a specific patient (by patientId UUID)
   * @param {string} patientId - Patient UUID
   * @returns {Promise<Array>} - Array of visit summaries
   */
  async getVisitsByPatientId (patientId) {
    try {
      const response = await api.get(`/visits/patient/${patientId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching visits for patient:', error);
      return [];
    }
  }

  /**
   * Get color based on elapsed time and thresholds
   * @param {number} seconds - Elapsed seconds
   * @param {number} warningThreshold - Warning threshold in seconds (default 300 = 5 min)
   * @param {number} dangerThreshold - Danger threshold in seconds (default 600 = 10 min)
   * @returns {string} - Color code ('success', 'warning', 'danger')
   */
  getTimingColor (seconds, warningThreshold = 300, dangerThreshold = 600) {
    if (seconds < warningThreshold) return 'success';
    if (seconds < dangerThreshold) return 'warning';
    return 'danger';
  }

  /**
   * Get wait time analytics
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (ISO string)
   * @param {string} params.endDate - End date (ISO string)
   * @param {string} params.eventId - Event ID filter (optional, 'all' for all events)
   * @returns {Promise} - API response with analytics data
   */
  async getWaitTimeAnalytics (params) {
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.eventId && params.eventId !== 'all') queryParams.append('eventId', params.eventId);
      
      const response = await api.get(`/visits/analytics/wait-times?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching wait time analytics:', error);
      throw error;
    }
  }

  /**
   * Get available events for analytics filtering
   * @returns {Promise} - API response with events list
   */
  async getAnalyticsEvents () {
    try {
      const response = await api.get('/visits/analytics/events');
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics events:', error);
      throw error;
    }
  }

  // ========================================================================
  // WebSocket Real-time Updates
  // ========================================================================

  /**
   * Connect to the patient queue WebSocket for real-time updates
   * @param {Function} onMessage - Callback for incoming messages
   * @param {Function} onOpen - Callback when connection opens
   * @param {Function} onClose - Callback when connection closes
   * @param {Function} onError - Callback for errors
   * @returns {Object} - WebSocket connection object with close method
   */
  connectToQueueWebSocket(onMessage, onOpen, onClose, onError) {
    if (!window.WebSocket) {
      console.error('WebSocket not supported');
      return null;
    }

    // Determine WebSocket URL based on current environment
    // Use the same host as the current page for WebSocket connection
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let host = window.location.host;
    
    // Try to extract host from REACT_APP_API_URL if it's a full URL
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) {
      try {
        // Check if it's a full URL (starts with http:// or https://)
        if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
          const parsedUrl = new URL(apiUrl);
          host = parsedUrl.host;
        }
        // If it's a relative path like '/api', use window.location.host (already set)
      } catch (e) {
        // If URL parsing fails, fall back to window.location.host
        console.warn('Could not parse REACT_APP_API_URL, using window.location.host');
      }
    }
    
    const wsUrl = `${protocol}//${host}/api/visits/ws`;

    console.log('📡 Connecting to Queue WebSocket:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    let pingInterval = null;

    ws.onopen = () => {
      console.log('📡 Connected to patient queue WebSocket');
      
      // Start ping/pong keep-alive every 30 seconds
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
      
      if (onOpen) onOpen();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Ignore pong messages
        if (data.type === 'pong') return;
        
        console.log('📡 Queue WebSocket message:', data.type);
        if (onMessage) onMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('📡 Queue WebSocket error:', error);
      if (onError) onError(error);
    };

    ws.onclose = () => {
      console.log('📡 Queue WebSocket connection closed');
      if (pingInterval) {
        clearInterval(pingInterval);
      }
      if (onClose) onClose();
    };

    // Return connection object with utility methods
    return {
      ws,
      close: () => {
        if (pingInterval) {
          clearInterval(pingInterval);
        }
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      },
      subscribe: (eventId) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'subscribe', eventId }));
        }
      },
      unsubscribe: () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'unsubscribe' }));
        }
      }
    };
  }
}

export default new VisitService();
